import type { EntityBatchSyncStatus } from './entityMutationSync'
import { shouldRetryProductionSyncOnReconnect } from './productionReconnectRetry'

export type BiomassReconnectDrainPlan = {
  shouldDrain: boolean
  productionRowIds: string[]
  mortalityLogIds: string[]
}

/**
 * Decides whether the Biomass screen should silently drain pending sync work on
 * a network/visibility reconnect, and returns the deterministic id buckets to
 * pass to the production-record and mortality-log retry helpers.
 *
 * Composes the generic `shouldRetryProductionSyncOnReconnect` predicate against
 * the merged production-record + mortality-log batch status that Biomass already
 * computes. Caller (Biomass) is responsible for actually invoking the retry
 * helpers and refreshing the merged status — this helper only answers "what
 * should I drain now?" so the decision stays unit-testable.
 *
 * Rules:
 *  - "hasEntityIds" for the underlying predicate is true when EITHER bucket has
 *    ids, since Biomass's banner reflects whichever bucket has pending work.
 *  - When `shouldDrain` is false, both id buckets are returned empty so the
 *    caller cannot accidentally drain stale ids.
 */
export const selectBiomassReconnectDrainTargets = (args: {
  status: EntityBatchSyncStatus | null
  isOnline: boolean
  productionRowIds: string[]
  mortalityLogIds: string[]
}): BiomassReconnectDrainPlan => {
  const hasEntityIds = args.productionRowIds.length > 0 || args.mortalityLogIds.length > 0
  const shouldDrain = shouldRetryProductionSyncOnReconnect({
    status: args.status,
    isOnline: args.isOnline,
    hasRowIds: hasEntityIds
  })
  return {
    shouldDrain,
    productionRowIds: shouldDrain ? args.productionRowIds : [],
    mortalityLogIds: shouldDrain ? args.mortalityLogIds : []
  }
}
